import { useEffect, useState } from 'react'
import { FocusTrapDialog } from '../../../components/FocusTrapDialog'
import { InlineAlert } from '../../../components/InlineAlert'
import { TimelinePanel } from '../../../components/timeline/TimelinePanel'
import { ApiError } from '../../../api/problem'
import {
  assignProfile,
  assignRole,
  assignTerritory,
  grantPermissionSet,
  listProfiles,
  listRoles,
  listTerritories,
  revokePermissionSet,
} from '../../../api/userAdmin'
import type { AssignmentView, ProfileSummary, RoleView, TerritoryView, UserView } from '../../../types/userAdmin'

export interface UserAssignmentDialogProps {
  user: UserView
  onClose: () => void
  /** Called after any assignment change that could affect the row shown in the list (Profile only —
   * PermissionSet/Role/Territory grants are not reflected in `UserView`). */
  onProfileChanged: (profileKey: string) => void
}

type Tab = 'assign' | 'history'

/**
 * Per-user Profile · Permission Sets · Role · Territory assignment editor (user-admin-entra-
 * federation Slice B, design §2.2, AC-B3) plus the per-user audit Timeline tab (AC-B5).
 *
 * <p><b>Honest scope note — no read-back for current grants.</b> Neither `AssignmentApi` nor any
 * Slice B endpoint exposes "list this user's currently-active Permission Sets/Role/Territories"
 * (the design's own endpoint table has no such GET). This editor can therefore only ever show
 * grants made IN THIS SESSION (their `assignmentId` is known right here, from each grant's own
 * response) — a previously-granted Permission Set from an earlier session cannot be listed or
 * revoked from this dialog. The History tab (real audit Timeline, `GET /events`) is the actual
 * source of truth for "what has this user been granted" — surfaced here rather than fabricating a
 * "current state" view this backend cannot honestly answer yet.
 */
export function UserAssignmentDialog({ user, onClose, onProfileChanged }: UserAssignmentDialogProps) {
  const [tab, setTab] = useState<Tab>('assign')

  const [profiles, setProfiles] = useState<ProfileSummary[]>([])
  const [roles, setRoles] = useState<RoleView[]>([])
  const [territories, setTerritories] = useState<TerritoryView[]>([])
  const [catalogError, setCatalogError] = useState<string | null>(null)

  const [profileKey, setProfileKey] = useState(user.profileKey ?? '')
  const [profileSaving, setProfileSaving] = useState(false)

  const [permissionSetKey, setPermissionSetKey] = useState('')
  const [grantedThisSession, setGrantedThisSession] = useState<AssignmentView[]>([])
  const [grantSaving, setGrantSaving] = useState(false)

  const [roleId, setRoleId] = useState('')
  const [roleSaving, setRoleSaving] = useState(false)

  const [territoryId, setTerritoryId] = useState('')
  const [addedTerritoryIds, setAddedTerritoryIds] = useState<string[]>([])
  const [territorySaving, setTerritorySaving] = useState(false)

  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([listProfiles(controller.signal), listRoles(controller.signal), listTerritories(controller.signal)])
      .then(([p, r, t]) => {
        setProfiles(p)
        setRoles(r)
        setTerritories(t)
      })
      .catch((err) => {
        if (err instanceof ApiError) setCatalogError(err.problem.detail ?? err.message)
        else setCatalogError('Could not load the Profile/Role/Territory catalogs.')
      })
    return () => controller.abort()
  }, [])

  async function handleAssignProfile() {
    if (!profileKey || profileKey === user.profileKey || profileSaving) return
    setProfileSaving(true)
    setError(null)
    setNotice(null)
    try {
      await assignProfile(user.id, profileKey)
      onProfileChanged(profileKey)
      setNotice(`Profile changed to ${profileKey}.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not change the Profile.')
    } finally {
      setProfileSaving(false)
    }
  }

  async function handleGrantPermissionSet() {
    if (!permissionSetKey.trim() || grantSaving) return
    setGrantSaving(true)
    setError(null)
    setNotice(null)
    try {
      const assignment = await grantPermissionSet(user.id, permissionSetKey.trim(), null)
      setGrantedThisSession((prev) => [...prev, assignment])
      setPermissionSetKey('')
      setNotice(`Granted ${assignment.key}.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not grant that Permission Set.')
    } finally {
      setGrantSaving(false)
    }
  }

  async function handleRevokePermissionSet(assignment: AssignmentView) {
    setError(null)
    setNotice(null)
    try {
      await revokePermissionSet(user.id, assignment.id)
      setGrantedThisSession((prev) => prev.filter((a) => a.id !== assignment.id))
      setNotice(`Revoked ${assignment.key}.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not revoke that Permission Set.')
    }
  }

  async function handleAssignRole() {
    if (!roleId || roleSaving) return
    setRoleSaving(true)
    setError(null)
    setNotice(null)
    try {
      await assignRole(user.id, roleId)
      const role = roles.find((r) => r.id === roleId)
      setNotice(`Role set to ${role?.name ?? roleId}.`)
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not assign that Role.')
    } finally {
      setRoleSaving(false)
    }
  }

  async function handleAddTerritory() {
    if (!territoryId || territorySaving) return
    setTerritorySaving(true)
    setError(null)
    setNotice(null)
    try {
      await assignTerritory(user.id, territoryId)
      setAddedTerritoryIds((prev) => (prev.includes(territoryId) ? prev : [...prev, territoryId]))
      const territory = territories.find((t) => t.id === territoryId)
      setNotice(`Added Territory ${territory?.name ?? territoryId}.`)
      setTerritoryId('')
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail ?? err.message : 'Could not add that Territory.')
    } finally {
      setTerritorySaving(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="user-assignment-title"
      title={`Manage access — ${user.name}`}
      onClose={onClose}
      width={640}
      footer={
        <button type="button" className="iw-btn iw-btn--primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <div role="tablist" aria-label="User assignment sections" style={{ display: 'flex', gap: 'var(--iw-space-2)', borderBottom: '1px solid var(--iw-border)', marginBottom: 'var(--iw-space-4)' }}>
        {(['assign', 'history'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            className="iw-btn iw-btn--ghost"
            style={tab === t ? { borderBottom: '2px solid var(--iw-accent)', borderRadius: 0, color: 'var(--iw-accent)' } : { borderRadius: 0 }}
            onClick={() => setTab(t)}
          >
            {t === 'assign' ? 'Assignments' : 'History'}
          </button>
        ))}
      </div>

      {tab === 'history' ? (
        <div role="tabpanel" aria-label="History">
          <TimelinePanel target={{ type: 'AppUser', id: user.id }} />
        </div>
      ) : (
        <div role="tabpanel" aria-label="Assignments" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-5)' }}>
          {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
          {notice ? <InlineAlert severity="success">{notice}</InlineAlert> : null}
          {catalogError ? <InlineAlert severity="warning">{catalogError}</InlineAlert> : null}

          <section>
            <h3 style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-sm)' }}>Profile</h3>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
              <select
                className="iw-select"
                aria-label="Profile"
                value={profileKey}
                onChange={(e) => setProfileKey(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="" disabled>
                  Select…
                </option>
                {profiles.map((p) => (
                  <option key={p.id} value={p.key}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="iw-btn"
                onClick={() => void handleAssignProfile()}
                disabled={profileSaving || !profileKey || profileKey === user.profileKey}
              >
                {profileSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </section>

          <section>
            <h3 style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-sm)' }}>Permission Sets</h3>
            <p style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
              No catalog endpoint exists yet for Permission Set keys — enter a known canon key (e.g.{' '}
              <code>PS.ScopeChange</code>, <code>PS.HardPurge</code>, <code>PS.BulkOps</code>) or a tenant-defined one.
            </p>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
              <input
                className="iw-input"
                aria-label="Permission Set key"
                placeholder="PS.ScopeChange"
                value={permissionSetKey}
                onChange={(e) => setPermissionSetKey(e.target.value)}
                style={{ flex: 1 }}
              />
              <button type="button" className="iw-btn" onClick={() => void handleGrantPermissionSet()} disabled={grantSaving || !permissionSetKey.trim()}>
                {grantSaving ? 'Granting…' : 'Grant'}
              </button>
            </div>
            {grantedThisSession.length > 0 ? (
              <ul style={{ listStyle: 'none', margin: 'var(--iw-space-2) 0 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-1)' }}>
                {grantedThisSession.map((a) => (
                  <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
                    <span style={{ flex: 1 }}>{a.key}</span>
                    <button type="button" className="iw-btn iw-btn--sm" onClick={() => void handleRevokePermissionSet(a)}>
                      Revoke
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section>
            <h3 style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-sm)' }}>Role</h3>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
              <select
                className="iw-select"
                aria-label="Role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="" disabled>
                  Select…
                </option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button type="button" className="iw-btn" onClick={() => void handleAssignRole()} disabled={roleSaving || !roleId}>
                {roleSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </section>

          <section>
            <h3 style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-sm)' }}>Territory</h3>
            <p style={{ margin: '0 0 var(--iw-space-2) 0', fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-muted)' }}>
              An actor may hold multiple Territories; there is no revoke endpoint yet, so Territories can
              only be added here.
            </p>
            <div style={{ display: 'flex', gap: 'var(--iw-space-2)' }}>
              <select
                className="iw-select"
                aria-label="Territory"
                value={territoryId}
                onChange={(e) => setTerritoryId(e.target.value)}
                style={{ flex: 1 }}
              >
                <option value="" disabled>
                  Select…
                </option>
                {territories.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <button type="button" className="iw-btn" onClick={() => void handleAddTerritory()} disabled={territorySaving || !territoryId}>
                {territorySaving ? 'Adding…' : 'Add'}
              </button>
            </div>
            {addedTerritoryIds.length > 0 ? (
              <ul style={{ listStyle: 'none', margin: 'var(--iw-space-2) 0 0 0', padding: 0 }}>
                {addedTerritoryIds.map((id) => {
                  const territory = territories.find((t) => t.id === id)
                  return <li key={id}>{territory?.name ?? id}</li>
                })}
              </ul>
            ) : null}
          </section>
        </div>
      )}
    </FocusTrapDialog>
  )
}
